import { getFallbackImageLetters, getUrlForImage, isTokenADA, isValidHexColor, isValidUrl } from '@/src/lib/SmartDB';
import { hexToStr } from '@/src/lib/MayzSmartDB';
import classNames from 'classnames';
import Image from 'next/image';
import { Fragment } from 'react';
import styles from './TokenLogoWithCircle.module.scss';

interface ITokenLogoProps {
    token: {
        CS: string;
        TN_Hex: string;
        ticker?: string;
        image?: string;
        colorHex?: string;
        percentage?: bigint;
    };
    showBorder?: boolean;
    width?: number;
    height?: number;
    className?: string;
    style?: React.CSSProperties;
}

const TokenLogo: React.FC<ITokenLogoProps> = ({ token, showBorder = true, width = 22, height = 22, className, style }) => {
    //--------------------------------------
    // Adjust size if border is enabled
    const circleWidth = Math.round(width * 1.8);
    const circleHeight = Math.round(height * 1.8);
    //--------------------------------------
    const colorHex = token.colorHex !== undefined && isValidHexColor(token.colorHex) ? token.colorHex : '#000000';
    const percentage = token.percentage !== undefined ? token.percentage : 100n;
    const ticker = token.ticker ?? hexToStr(token.TN_Hex);
    const tokenName = isTokenADA(token.CS, token.TN_Hex) ? `` : ` [${hexToStr(token.TN_Hex)}]`;
    //--------------------------------------
    return (
        <Fragment>
            <div
                className={classNames(styles.logo, className)}
                style={{
                    width: `${width}px`,
                    height: `${width}px`,
                    ...style, // Allow custom styles
                }}
                title={`${ticker}${tokenName}`}
            >
                <div className={styles.tokenLogo}>
                    {token.image && isValidUrl(token.image) ? (
                        <Image
                            src={getUrlForImage(token.image)}
                            width="100%"
                            height="100%"
                            layout="responsive"
                            objectFit="contain"
                            alt={ticker}
                            title={`${ticker}${tokenName}`}
                            className={styles.tokenImage}
                        />
                    ) : (
                        <div
                            className={styles.tokenImageFallback}
                            title={`${ticker}${tokenName}`}
                            style={{
                                borderColor: colorHex,
                            }}
                        >
                            <div
                                className={styles.tokenImageFallbackText}
                                style={{
                                    color: colorHex,
                                    fontSize: `${width * 0.5}px`, // Dynamically scales font size
                                }}
                            >
                                {getFallbackImageLetters(token)}
                            </div>
                        </div>
                    )}
                </div>

                {showBorder && (
                    <div
                        className={styles.border}
                        title={`${ticker}${tokenName}`}
                        style={{
                            width: `${circleWidth}px`,
                            height: `${circleHeight}px`,
                        }}
                    >
                        <svg className={styles.fill} style={{ stroke: `${colorHex}` }}>
                            <circle
                                r="16"
                                cx="50%"
                                cy="50%"
                                pathLength="100"
                                style={{
                                    fill: 'none',
                                    stroke: '#636363', // Background circle color
                                    strokeWidth: '4',
                                }}
                            />
                            <circle
                                r="16"
                                cx="50%"
                                cy="50%"
                                pathLength={100}
                                style={{
                                    strokeDasharray: `${percentage} 100`,
                                }}
                            />
                        </svg>
                    </div>
                )}
            </div>
        </Fragment>
    );
};

export default TokenLogo;
